/**
 * Timestamps are stored in UTC. Business/reporting dates use the workspace
 * timezone, which for version 1 is always Asia/Karachi (plan section 7, 12).
 */

export const WORKSPACE_TIMEZONE = 'Asia/Karachi'

function toDate(input: Date | string | number): Date {
  const d = input instanceof Date ? input : new Date(input)
  if (Number.isNaN(d.getTime())) throw new RangeError(`Invalid date: ${String(input)}`)
  return d
}

/** Current time as a UTC ISO-8601 string. */
export function nowUtcIso(): string {
  return new Date().toISOString()
}

/** The calendar date (YYYY-MM-DD) of an instant, evaluated in Asia/Karachi. */
export function karachiBusinessDate(input: Date | string | number = new Date()): string {
  const d = toDate(input)
  const parts = new Intl.DateTimeFormat('en-CA', {
    timeZone: WORKSPACE_TIMEZONE,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  }).formatToParts(d)
  const get = (t: string) => parts.find((p) => p.type === t)?.value ?? ''
  return `${get('year')}-${get('month')}-${get('day')}`
}

/** Human-readable date-time in Asia/Karachi for display, e.g. "12 Oct 2026, 4:05 pm PKT". */
export function formatKarachiDateTime(input: Date | string | number): string {
  const d = toDate(input)
  return new Intl.DateTimeFormat('en-GB', {
    timeZone: WORKSPACE_TIMEZONE,
    dateStyle: 'medium',
    timeStyle: 'short',
  }).format(d)
}

/**
 * Inclusive [startUtc, endUtc) ISO bounds for a business date (YYYY-MM-DD)
 * interpreted in Asia/Karachi. Used by report date filters so a "day" means a
 * Karachi day, not a UTC day.
 */
export function karachiDayBoundsUtc(businessDate: string): { startIso: string; endIso: string } {
  if (!/^\d{4}-\d{2}-\d{2}$/.test(businessDate)) {
    throw new RangeError(`Expected YYYY-MM-DD, got "${businessDate}"`)
  }
  // Karachi is UTC+5 with no DST since 2002, but derive the offset via Intl
  // rather than hardcoding, so historical/edge instants stay correct.
  const startUtcMs = zonedTimeStartUtcMs(businessDate)
  const endUtcMs = zonedTimeStartUtcMs(shiftDate(businessDate, 1))
  return {
    startIso: new Date(startUtcMs).toISOString(),
    endIso: new Date(endUtcMs).toISOString(),
  }
}

function shiftDate(businessDate: string, days: number): string {
  const [y, m, d] = businessDate.split('-').map(Number)
  const dt = new Date(Date.UTC(y, m - 1, d + days))
  return dt.toISOString().slice(0, 10)
}

/** UTC millisecond instant at which a Karachi calendar date begins (00:00 PKT). */
function zonedTimeStartUtcMs(businessDate: string): number {
  const [y, m, d] = businessDate.split('-').map(Number)
  const naiveMidnightAsUtc = Date.UTC(y, m - 1, d, 0, 0, 0)
  const offsetMinutes = karachiOffsetMinutes(new Date(naiveMidnightAsUtc))
  return naiveMidnightAsUtc - offsetMinutes * 60 * 1000
}

/** Zone offset in minutes (local - UTC) for Asia/Karachi at a given instant. */
function karachiOffsetMinutes(at: Date): number {
  const parts = new Intl.DateTimeFormat('en-US', {
    timeZone: WORKSPACE_TIMEZONE,
    hour12: false,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
  }).formatToParts(at)
  const map: Record<string, string> = {}
  for (const p of parts) map[p.type] = p.value
  const wallAsUtc = Date.UTC(
    Number(map.year),
    Number(map.month) - 1,
    Number(map.day),
    Number(map.hour) % 24,
    Number(map.minute),
    Number(map.second),
  )
  return Math.round((wallAsUtc - at.getTime()) / 60000)
}
